/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import { useIntl } from 'react-intl-next';

import Button from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import StatusWarningIcon from '@atlaskit/icon/core/status-warning';
import { Inline } from '@atlaskit/primitives/compiled';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { token } from '@atlaskit/tokens';

import messages from './messages';

const styles = cssMap({
	message: {
		color: token('color.text'),
	},
	actions: {
		marginLeft: 'auto',
	},
});

type WarningViewProps = {
	onKeep: () => void;
	onDelete: () => void;
	willDeleteAfterAbort: boolean;
};

export const WarningView = ({
	onKeep,
	onDelete,
	willDeleteAfterAbort,
}: WarningViewProps): JSX.Element => {
	const { formatMessage } = useIntl();

	const renderMessage = () => (
		<>
			<StatusWarningIcon
				color={token('color.icon.warning')}
				spacing="spacious"
				size="medium"
				label=""
			/>
			{formatMessage(willDeleteAfterAbort ? messages.discardMessage : messages.stopMessage)}
		</>
	);

	const renderButtons = () => (
		<>
			<Button autoFocus appearance="subtle" onClick={onKeep}>
				{formatMessage(willDeleteAfterAbort ? messages.keepButtonLabel : messages.noButtonLabel)}
			</Button>
			<Button appearance={willDeleteAfterAbort ? 'warning' : 'default'} onClick={onDelete}>
				{formatMessage(willDeleteAfterAbort ? messages.deleteButtonLabel : messages.yesButtonLabel)}
			</Button>
		</>
	);

	if (expValEquals('platform_editor_comment_rovoinlinechat_improvement', 'isEnabled', true)) {
		return (
			<>
				<Inline
					testId="inline-chat__warning-view"
					space="space.100"
					alignBlock="center"
					xcss={styles.message}
				>
					{renderMessage()}
				</Inline>
				<Inline space="space.100" xcss={styles.actions}>
					{renderButtons()}
				</Inline>
			</>
		);
	}

	return (
		<Inline
			testId="inline-chat__warning-view"
			spread="space-between"
			alignBlock="center"
			space="space.100"
		>
			<Inline space="space.100" alignBlock="center" xcss={styles.message}>
				{renderMessage()}
			</Inline>
			<Inline space="space.100">{renderButtons()}</Inline>
		</Inline>
	);
};
